<template>
  <!-- focusin once: the first interaction with any field marks the start of the funnel (#6321);
       a page view alone does not, and "Opnieuw berekenen" stays the same session. -->
  <form ref="root" class="flex flex-col gap-8" novalidate @submit.prevent="submit" @focusin.once="onFirstInteraction">
    <div class="flex flex-col gap-5">
      <NumberField
        id="electricity-usage"
        v-model="input.electricityUsage"
        label="Stroom afname"
        placeholder="1234"
        unit="kWh per jaar"
        :icon="lightning"
        info="Het totale stroomverbruik van je huishouden per jaar, in kilowattuur. Je vindt dit op je jaarafrekening."
        :error="input.fieldErrors.electricityUsage"
        :locked="locked"
        :flash="estimateFlash"
        :flash-delay="0"
      />

      <NumberField
        id="generation"
        v-model="input.generation"
        label="Stroom terugleveren"
        placeholder="1234"
        unit="kWh per jaar"
        :icon="sunPanel"
        info="De stroom die de omvormer van je zonnepanelen per jaar aan het net teruglevert, in kilowattuur. Je vindt dit op je jaarafrekening."
        :error="input.fieldErrors.generation"
        :locked="locked"
        :flash="estimateFlash"
        :flash-delay="150"
      />

      <div class="flex flex-col gap-1.5">
        <NumberField
          id="gas-usage"
          v-model="input.gasUsage"
          label="Gasverbruik"
          placeholder="1234"
          unit="m³ per jaar"
          :icon="gasFlame"
          info="Het gasverbruik van je huishouden per jaar, in kubieke meters. Je vindt dit op je jaarafrekening."
          :error="input.fieldErrors.gasUsage"
          :disabled="!input.hasGasConnection"
          :locked="locked"
          :flash="estimateFlash && input.hasGasConnection"
          :flash-delay="300"
        />
        <CheckboxField id="no-gas-connection" v-model="noGasConnection" :disabled="locked">
          Ik heb geen gasaansluiting
        </CheckboxField>
      </div>

      <div class="flex items-center gap-1">
        <button
          ref="estimationLink"
          type="button"
          :disabled="locked"
          class="-my-[11px] cursor-pointer rounded-sm py-[11px] text-[14px] leading-[22px] font-medium text-label underline transition-colors hover:text-heading focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-bright disabled:cursor-default"
          @click="estimationOpen = true"
        >
          Help mijn verbruik inschatten
        </button>
        <InfoButton
          subject="verbruik inschatten"
          text="Weet je je verbruik niet? Op basis van het aantal personen in je huishouden en je zonnepanelen vullen we een gemiddelde voor je in."
        />
      </div>

      <div class="grid gap-5 sm:grid-cols-2">
        <SelectField
          id="contract-type"
          v-model="contractType"
          label="Welk type contract heb je nu?"
          :options="CONTRACT_TYPES"
          info="Het type energiecontract dat je nu hebt. Je vindt dit in je contract of in de app van je energieleverancier."
          :hint="contractTypeHint"
          :error="input.fieldErrors.contractType"
          :disabled="locked"
        />
        <div class="flex flex-col gap-1.5">
          <DateField
            id="contract-end"
            v-model="input.contractEndDate"
            :min="minEndDate"
            label="Wanneer eindigt je contract?"
            :error="input.fieldErrors.contractEndDate"
            :disabled="input.contractEndUnknown"
            :locked="locked"
          />
          <!-- A contract type without an end date ticks this box through the store; the box then
               freezes so the greyed-out date field cannot be reopened while that type is chosen. -->
          <CheckboxField
            id="contract-end-unknown"
            v-model="input.contractEndUnknown"
            :disabled="locked || !contractHasEndDate(input.contractType)"
          >
            Weet ik niet / niet van toepassing
          </CheckboxField>
        </div>
      </div>
    </div>

    <!-- What capi rejected without pointing at a field the form shows. -->
    <div
      v-if="input.generalErrors.length"
      role="alert"
      class="flex flex-col gap-1 rounded-xl border border-red-600 bg-red-50 p-4 text-[14px] font-medium text-red-700"
    >
      <p v-for="message in input.generalErrors" :key="message">{{ message }}</p>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-6">
        <Button type="submit" :started="locked">{{ locked ? 'Berekening gestart' : 'Start berekening' }}</Button>
        <button
          v-if="step === 'result'"
          type="button"
          class="-my-[11px] cursor-pointer rounded-sm py-[11px] text-[14px] leading-[22px] font-medium text-label underline transition-colors hover:text-heading focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-bright"
          @click="emit('restart')"
        >
          Opnieuw berekenen
        </button>
      </div>
      <!-- The count comes from capi (#6361). Without it the claim would be a guess, so the line
           stays away until the number is in. -->
      <div v-if="step === 'input' && calculationCount !== null" class="flex items-start gap-1.5 pl-4">
        <img :src="arrowDoodle" alt="" class="mt-1 w-[33px]" />
        <p class="max-w-[204px] pt-4 text-[14px] font-medium text-body">
          Al {{ formatNumber(calculationCount) }} huishoudens gingen je voor!
        </p>
      </div>
    </div>

    <EstimationModal v-if="estimationOpen" @close="closeEstimation" @applied="flashEstimate" />

    <!-- The visual counterpart is the flash on the three fields. -->
    <p class="sr-only" role="status">{{ estimateFlash ? 'De schatting staat in de invoervelden.' : '' }}</p>
  </form>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'

import arrowDoodle from '@/assets/svg/arrow-doodle.svg'
import gasFlame from '@/assets/svg/gas-flame.svg'
import lightning from '@/assets/svg/lightning.svg'
import sunPanel from '@/assets/svg/sun-panel.svg'
import Button from '@/components/Button.vue'
import EstimationModal from '@/components/EstimationModal.vue'
import CheckboxField from '@/components/form/CheckboxField.vue'
import DateField from '@/components/form/DateField.vue'
import InfoButton from '@/components/form/InfoButton.vue'
import NumberField from '@/components/form/NumberField.vue'
import SelectField from '@/components/form/SelectField.vue'
import { trackStart } from '@/services/analytics'
import { requestCalculationCount } from '@/services/statistics'
import { useCalculationStore } from '@/stores/calculation'
import { useInputStore } from '@/stores/input'
import { CONTRACT_TYPE_HINTS, CONTRACT_TYPES, type ContractType, contractHasEndDate } from '@/types/calculation'
import type { Step } from '@/types/flow'
import { formatNumber } from '@/utils/number'

const props = defineProps<{
  /** The form stays on screen through the whole flow, frozen once the calculation starts. */
  step: Step
}>()

const emit = defineEmits<{ submit: []; restart: [] }>()

const input = useInputStore()
const calculation = useCalculationStore()

const root = useTemplateRef('root')

// Frozen during the request too: capi does the checking, so the answer decides what happens next.
const locked = computed(() => props.step !== 'input' || calculation.isCalculating)

// The design asks "heb ik geen gas?", the store keeps the positive fact.
const noGasConnection = computed({
  get: () => !input.hasGasConnection,
  set: (value) => (input.hasGasConnection = !value),
})

// The select speaks plain strings, the store the ContractType union. The options list only
// contains union values, so the cast narrows what the select already guaranteed.
const contractType = computed({
  get: () => input.contractType,
  set: (value) => (input.contractType = value as ContractType),
})

const contractTypeHint = computed(() =>
  input.contractType === '' ? undefined : CONTRACT_TYPE_HINTS[input.contractType],
)

// The contract end date must lie in the future, so the picker starts at tomorrow. Built from the
// local date parts: toISOString would shift to UTC and hand out the wrong day around midnight.
const tomorrow = new Date()
tomorrow.setDate(tomorrow.getDate() + 1)
const minEndDate = [
  tomorrow.getFullYear(),
  String(tomorrow.getMonth() + 1).padStart(2, '0'),
  String(tomorrow.getDate()).padStart(2, '0'),
].join('-')

// How many calculations the tool has made. Null while it is being fetched and after a failed
// fetch: the line disappears rather than showing a number that is not the real total.
const calculationCount = ref<number | null>(null)

onMounted(async () => {
  try {
    calculationCount.value = await requestCalculationCount()
  } catch {
    calculationCount.value = null
  }
})

const estimationOpen = ref(false)
const estimationLink = useTemplateRef('estimationLink')

// Long enough for the staggered fade-outs plus a beat, then the class leaves so a next apply
// flashes again.
const estimateFlash = ref(false)

let flashTimer: ReturnType<typeof setTimeout>

function flashEstimate() {
  estimateFlash.value = true
  clearTimeout(flashTimer)
  flashTimer = setTimeout(() => (estimateFlash.value = false), 1800)
}

onBeforeUnmount(() => clearTimeout(flashTimer))

function closeEstimation() {
  estimationOpen.value = false
  // The keyboard picks up where it left off: on the link that opened the modal.
  estimationLink.value?.focus()
}

function onFirstInteraction() {
  trackStart(input.contractType)
}

function submit() {
  if (locked.value) {
    return
  }

  // The request itself runs in App.vue, which pairs the answer with the calculation animation.
  emit('submit')
}

// capi rejected the input: the message text sits next to the field, so put the keyboard there too.
watch(
  () => calculation.status,
  async (status) => {
    if (status !== 'invalid') {
      return
    }

    await nextTick()
    root.value?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
  },
)
</script>
